import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { apiFetch } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { Modal } from '../components/ui/Modal'
import type { OrgMember } from '../types'

interface MemberRow extends OrgMember {
  email?: string
  must_change_password?: boolean
}

export function TeamPage() {
  const { profile } = useAuth()
  const { toast } = useToast()
  const { confirm, dialog: confirmDialog } = useConfirm()

  const [members, setMembers] = useState<MemberRow[]>([])
  const [loading, setLoading] = useState(true)

  // Invite state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'org_admin' | 'export' | 'viewer'>('viewer')
  const [inviting, setInviting] = useState(false)

  // Temp password modal
  const [tempPasswordModal, setTempPasswordModal] = useState<{ email: string; password: string } | null>(null)

  // Password management modal
  const [pwModal, setPwModal] = useState<MemberRow | null>(null)
  const [pwMode, setPwMode] = useState<'auto' | 'custom'>('auto')
  const [pwCustom, setPwCustom] = useState('')
  const [pwMustChange, setPwMustChange] = useState(true)
  const [pwSaving, setPwSaving] = useState(false)

  const orgId = profile?.org_id

  const loadMembers = useCallback(async () => {
    if (!orgId) return
    try {
      const data = await apiFetch<MemberRow[]>(`/api/admin/members?org_id=${orgId}`)
      setMembers(data ?? [])
    } catch {
      setMembers([])
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => { loadMembers() }, [loadMembers])

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!orgId || !inviteEmail.trim()) return
    setInviting(true)

    try {
      const result = await apiFetch<{
        status: string
        email: string
        temp_password?: string
      }>('/api/admin/members', {
        method: 'POST',
        body: JSON.stringify({
          org_id: orgId,
          email: inviteEmail.trim(),
          role: inviteRole,
        }),
      })
      setInviteEmail('')
      await loadMembers()

      if (result.status === 'created' && result.temp_password) {
        setTempPasswordModal({ email: result.email, password: result.temp_password })
      } else {
        toast(`${result.email} added to team`, 'success')
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to add user', 'error')
    } finally {
      setInviting(false)
    }
  }

  function openPasswordModal(member: MemberRow) {
    setPwModal(member)
    setPwMode('auto')
    setPwCustom('')
    setPwMustChange(true)
  }

  async function handleSetPassword() {
    if (!orgId || !pwModal) return
    setPwSaving(true)

    const payload: Record<string, unknown> = {
      user_id: pwModal.user_id,
      org_id: orgId,
      must_change_password: pwMustChange,
    }
    if (pwMode === 'custom') {
      if (pwCustom.length < 8) {
        toast('Password must be at least 8 characters', 'error')
        setPwSaving(false)
        return
      }
      payload.password = pwCustom
    }

    try {
      const result = await apiFetch<{ temp_password: string }>('/api/admin/members', {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
      setPwModal(null)
      setTempPasswordModal({
        email: pwModal.email ?? pwModal.user_id,
        password: result.temp_password,
      })
      await loadMembers()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to set password', 'error')
    } finally {
      setPwSaving(false)
    }
  }

  async function handleRemoveMember(member: MemberRow) {
    if (!orgId) return
    const ok = await confirm({
      title: 'Remove Member',
      message: `Are you sure you want to remove ${member.email ?? member.user_id} from the team?`,
      confirmLabel: 'Remove',
    })
    if (!ok) return

    try {
      await apiFetch('/api/admin/members', {
        method: 'DELETE',
        body: JSON.stringify({
          member_id: member.id,
          org_id: orgId,
        }),
      })
      toast('Member removed', 'success')
      await loadMembers()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove member', 'error')
    }
  }

  async function handleRoleChange(member: MemberRow, newRole: string) {
    if (!orgId || newRole === member.role) return
    try {
      await apiFetch('/api/admin/members', {
        method: 'PUT',
        body: JSON.stringify({ user_id: member.user_id, org_id: orgId, role: newRole }),
      })
      toast(`Role updated to ${newRole.replace('_', ' ')}`, 'success')
      await loadMembers()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update role', 'error')
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text)
    toast('Copied to clipboard', 'info')
  }

  const inputCls = 'flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const selectCls = 'bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const btnPrimary = 'bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50'
  const btnSmall = 'text-xs text-gray-500 hover:text-gray-900 px-2 py-1 transition-colors'

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Team</h1>
        <p className="text-gray-500 text-sm mt-1">
          Manage members of {profile?.org_name ?? 'your organization'}
        </p>
      </div>

      {/* Invite form */}
      <form onSubmit={handleInvite} className="flex gap-2 mb-6">
        <input
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="user@email.com"
          className={inputCls}
        />
        <select
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as 'org_admin' | 'export' | 'viewer')}
          className={selectCls}
        >
          <option value="viewer">Viewer</option>
          <option value="export">Export</option>
          <option value="org_admin">Org Admin</option>
        </select>
        <button type="submit" disabled={inviting} className={btnPrimary}>
          {inviting ? '...' : 'Add Member'}
        </button>
      </form>

      {/* Members list */}
      {loading ? (
        <p className="text-gray-500 text-sm">Loading...</p>
      ) : members.length > 0 ? (
        <div className="space-y-2">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between p-3 bg-card border border-card-border rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm font-medium">{m.email ?? m.user_id}</p>
                  {m.must_change_password && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning font-medium">
                      Temp password
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={m.role}
                  onChange={(e) => handleRoleChange(m, e.target.value)}
                  className="text-xs bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-gray-700 focus:outline-none focus:border-brand"
                >
                  {profile?.role === 'rv_admin' && <option value="rv_admin">RV Admin</option>}
                  <option value="org_admin">Org Admin</option>
                  <option value="export">Export</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button
                  onClick={() => openPasswordModal(m)}
                  className={btnSmall}
                  title="Manage password"
                >
                  Password
                </button>
                <button
                  onClick={() => handleRemoveMember(m)}
                  className="text-xs text-gray-400 hover:text-error px-2 py-1 transition-colors"
                  title="Remove from team"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-card-border rounded-lg p-8 text-center text-gray-500">
          <p>No team members yet. Add someone above.</p>
        </div>
      )}

      {/* Password management modal */}
      <Modal
        open={!!pwModal}
        onClose={() => setPwModal(null)}
        title="Set Password"
      >
        {pwModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Set password for <span className="font-medium text-gray-900">{pwModal.email ?? pwModal.user_id}</span>
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPwMode('auto')}
                className={`flex-1 text-sm py-2 rounded-md border transition-colors ${
                  pwMode === 'auto'
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                Auto Generate
              </button>
              <button
                type="button"
                onClick={() => setPwMode('custom')}
                className={`flex-1 text-sm py-2 rounded-md border transition-colors ${
                  pwMode === 'custom'
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                Custom Password
              </button>
            </div>
            {pwMode === 'custom' && (
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">Password</label>
                <input
                  type="text"
                  value={pwCustom}
                  onChange={(e) => setPwCustom(e.target.value)}
                  placeholder="Min 8 characters"
                  className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20"
                  autoFocus
                />
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setPwMustChange(!pwMustChange)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  pwMustChange ? 'bg-brand' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  pwMustChange ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </div>
              <span className="text-sm text-gray-700">Require password change on login</span>
            </label>
            <button
              onClick={handleSetPassword}
              disabled={pwSaving || (pwMode === 'custom' && pwCustom.length < 8)}
              className="w-full bg-brand hover:bg-brand-dark text-white text-sm font-medium py-2 rounded-md transition-colors disabled:opacity-50"
            >
              {pwSaving ? 'Saving...' : pwMode === 'auto' ? 'Generate & Set Password' : 'Set Password'}
            </button>
          </div>
        )}
      </Modal>

      {/* Temp password result modal */}
      <Modal
        open={!!tempPasswordModal}
        onClose={() => setTempPasswordModal(null)}
        title="Password Set"
      >
        {tempPasswordModal && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Share these credentials with the user.</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Email</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gray-900 flex-1">{tempPasswordModal.email}</code>
                  <button onClick={() => copyToClipboard(tempPasswordModal.email)} className="text-xs text-brand hover:text-brand-dark transition-colors">Copy</button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Password</label>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gray-900 font-bold flex-1">{tempPasswordModal.password}</code>
                  <button onClick={() => copyToClipboard(tempPasswordModal.password)} className="text-xs text-brand hover:text-brand-dark transition-colors">Copy</button>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                copyToClipboard(`Email: ${tempPasswordModal.email}\nPassword: ${tempPasswordModal.password}`)
                setTempPasswordModal(null)
              }}
              className="w-full bg-brand hover:bg-brand-dark text-white text-sm font-medium py-2 rounded-md transition-colors"
            >
              Copy All & Close
            </button>
          </div>
        )}
      </Modal>

      {confirmDialog}
    </div>
  )
}
